import { CropConstants } from './constants/Crop';
import { isBrazil } from './countryCode';

/**
 * Brazil reports historical yield in t/ha but the UI (and the grower) speaks in
 * sacos/ha. 1 saco = 60 kg, so 1 t/ha = 1000/60 = 16.67 sacos/ha.
 */
export const BRAZIL_SACOS_PER_TONNE = 16.67;

export const SACOS_PER_HA = 'sacos/ha';
export const TONNES_PER_HA = 't/ha';

const BRAZIL_CORN_YIELD_LOW_LABEL = '<100';
const BRAZIL_CORN_YIELD_MID_LABEL = '100-140';
const BRAZIL_CORN_YIELD_HIGH_LABEL = '>140';

const isCornCrop = (cropName?: string): boolean => {
  const normalizedCropName = cropName?.toLowerCase().trim();

  return normalizedCropName
    ? [
      CropConstants.CORN_SMALLCASE,
      CropConstants.GRAIN_CORN,
      CropConstants.SILAGE_CORN_SMALLCASE,
      CropConstants.SUMMER_CORN_SMALLCASE,
      CropConstants.WINTER_CORN.toLowerCase(),
    ].includes(normalizedCropName)
    : false;
};

const bucketBrazilCornYieldRange = (convertedYieldRange: string): string => {
  if (convertedYieldRange.includes('>')) {
    return BRAZIL_CORN_YIELD_HIGH_LABEL;
  }

  if (convertedYieldRange.includes('<')) {
    return BRAZIL_CORN_YIELD_LOW_LABEL;
  }

  const numbers = convertedYieldRange
    .match(/\d+\.?\d*/g)
    ?.map((value) => Number.parseFloat(value)) || [];

  if (numbers.length === 0) {
    return convertedYieldRange;
  }

  const maxValue = Math.max(...numbers);

  if (maxValue > 140) {
    return BRAZIL_CORN_YIELD_HIGH_LABEL;
  }

  if (maxValue >= 100) {
    return BRAZIL_CORN_YIELD_MID_LABEL;
  }

  return BRAZIL_CORN_YIELD_LOW_LABEL;
};

/**
 * Converts a t/ha yield range ("6-8.5", ">9.5") into sacos/ha, preserving the
 * original shape of the string. Returns undefined when there is nothing numeric
 * to convert, so callers can fall back to the raw value.
 *
 * Rounding matches the UI: DDG recommendations and soybeans keep the exact
 * rounded value, everything else is rounded to the nearest 10.
 */
export const convertYieldRangeToSacos = (
  yieldRange: string | undefined,
  { isDDG, cropName }: { isDDG: boolean; cropName?: string }
): string | undefined => {
  if (!yieldRange) {
    return undefined;
  }

  const isSoybeans = cropName?.toLowerCase() === CropConstants.SOYBEANS_SMALLCASE;
  const useExactValue = isDDG || isSoybeans;

  const operators = yieldRange.match(/[<>]/g) || [];
  const numbers = yieldRange.match(/\d+\.?\d*/g) || [];

  if (numbers.length === 0) {
    return undefined;
  }

  const converted = numbers.map((num) => {
    const exactValue = Number.parseFloat(num) * BRAZIL_SACOS_PER_TONNE;
    return useExactValue ? Math.round(exactValue) : Math.round(exactValue / 10) * 10;
  });

  const formattedYieldRange = converted.length === 2
    ? `${converted[0]}-${converted[1]}`
    : `${operators[0] || ''}${converted[0]}`;

  if (isCornCrop(cropName)) {
    return bucketBrazilCornYieldRange(formattedYieldRange);
  }

  return formattedYieldRange;
};

/**
 * The unit the yield range is expressed in once converted for display.
 * Brazil is always sacos/ha; elsewhere we trust the unit the solution carries.
 */
export const getYieldRangeUnit = (
  country: string | undefined,
  solutionYieldRangeUnit?: string
): string => {
  if (isBrazil(country)) {
    return SACOS_PER_HA;
  }
  return solutionYieldRangeUnit || TONNES_PER_HA;
};
